import Button from "@mui/material/Button";
import {
  format,
  formatDuration,
  intervalToDuration,
  parseISO,
} from "date-fns";
import { useRef, useEffect, useState } from "react";
import "./AssignmentDescription.scss";
import { shortDurationLocale } from "utils/time";
import LoadingImage from "components/LoadingImage";
import { useTranslation } from "react-i18next";
import MovingBorderButton from "components/MovingBorderButton";
import { HourglassEmpty } from "@mui/icons-material";

export default function AssignmentDescription(props: {
  lab?: any;
  handleScheduleInstance?;
  refreshReservation?;
  handleOpenAuthModal?;
  reservation?;
  showLogin?: boolean;
  showError?: boolean;
  showCooking?: boolean;
  showBooked?: boolean;
  showReady?: boolean;
  showStartNow?: boolean;
  submissionError?: string;
  isLoading?: boolean;
  setSubmissionError?: (error: string) => void;
  setIsLoading?: (loading: boolean) => void;
}) {
  const {
    lab,
    reservation,
    refreshReservation,
    handleOpenAuthModal,
    handleScheduleInstance,
    showError,
    showCooking,
    showBooked,
    showReady,
    showStartNow,
    showLogin,
    submissionError,
    isLoading,
    setSubmissionError,
    setIsLoading,
  } = props;

  const { t } = useTranslation();
  // const navigate = useNavigate();

  const descriptionRef = useRef(null);
  const [descriptionHasOverflow, setDescriptionHasOverflow] = useState(false);
  const [descriptionShowMore, setDescriptionShowMore] = useState(false);

  useEffect(() => {
    if (descriptionRef && descriptionRef.current) {
      const hasOverflowingChildren =
        descriptionRef.current.offsetHeight <
        descriptionRef.current.scrollHeight ||
        descriptionRef.current.offsetWidth < descriptionRef.current.scrollWidth;
      setDescriptionHasOverflow(hasOverflowingChildren);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lab]);

  return (
    <section className="assignment_desc">
      <div className="section-left">
        <img
          src={
            lab.illustration
              ? lab.illustration
              : require("assets/lab_desc_illust.png")
          }
          alt={t("lab")}
          className="illustration"
        />
      </div>
      <div className="content">
        <h1 className="hero_title">{lab.name}</h1>
        <div className="heroes">
          {/* <div className="hero_item">
            <img
              src={require("assets/task.svg").default}
              alt={t("task")}
              className="icon"
            />
            <h5 className="text">
              {lab._tasks} task{lab._tasks === 1 ? "" : "s"}
            </h5>
          </div> */}
          <div className="hero_item">
            <img
              src={require("assets/clock.svg").default}
              alt={t("estimated-time")}
              className="icon"
            />
            <h5 className="text">
              {formatDuration(
                intervalToDuration({
                  start: 0,
                  end: lab?.duration * 60 * 1000,
                }),
                {
                  format: ["hours", "minutes", "seconds"],
                  locale: shortDurationLocale,
                },
              )}
            </h5>
          </div>
          {/* <div className="hero_item">
            <img
              src={require("assets/star.svg").default}
              alt={t("rating")}
              className="icon"
            />
            <h5 className="text">
              {lab._rates} star
              {lab._rates === 1 ? "" : "s"}
            </h5>
          </div> */}
        </div>
        <div className="description">
          <h4 className="description_title">Description:</h4>
          <h4
            ref={descriptionRef}
            className={`text ${descriptionShowMore ? "":"clamp"}`}
          >
            {lab.description}
          </h4>
          {descriptionHasOverflow &&
            (!descriptionShowMore ? (
              <Button
                onClick={() => {
                  setDescriptionShowMore(true);
                }}
              >
                {t("show-more")}{" "}
              </Button>
            ) : (
              <Button onClick={() => setDescriptionShowMore(false)}>
                {t("show-less")}{" "}
              </Button>
            ))}
        </div>
        <>
          <div className="assignment_scheduling_btn_group">
            {/* Removed isInstancePageReady check, navigation is now automatic */}
            {reservation && (
              <>
                {showError && (
                  // Explicitly render error if status is failed
                  <div className="scheduling-errors text-center text-red-DEFAULT custom-text-[poppins-500-16px]">
                    {submissionError || t("reservation-failed-contact-support", "Reservation failed. Please try again.")}
                  </div>
                )}
                {reservation.date && (
                  <>
                    {showCooking && (
                      <MovingBorderButton
                        borderRadius="20px"
                        containerClassName="reservation-group h-[71px] w-full max-w-[566px] shadow"
                        className="btn reservation !h-[69px] !w-full !max-w-[564px] !border-none bg-white text-black !shadow-none"
                        duration={2000}
                      >
                        <h4 className="text">
                          <HourglassEmpty className="animate-spin" />{" "}
                          {t("its-cooking")}
                        </h4>
                      </MovingBorderButton>
                    )}
                    {showBooked && (
                      <div className="reservation-group">
                        <Button
                          className="btn reservation"
                        >
                          <h4 className="text booked-text">
                            ⚡{" "}
                            {format(
                              parseISO(reservation.date),
                              `'${t("booked-for-the")}' do LLLL uuuu '${t("at")}' hh:mm a`,
                            )}{" "}
                            ⚡
                          </h4>
                        </Button>
                        <h5>{t("see-you-soon")}</h5>
                      </div>
                    )}
                    {showReady && (
                      <div className="reservation-group">
                        {/* This state will be handled in the AssignmentsProvider. Automatically navigates to the instance page when ready. */}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {isLoading && (
              <LoadingImage />
            )}
            {showStartNow && (
              <Button
                onClick={async () => {
                  setSubmissionError(null);
                  setIsLoading(true);
                  let err = await handleScheduleInstance(new Date(), true);
                  if (!err) {
                    refreshReservation();
                  } else {
                    setSubmissionError(err);
                    setIsLoading(false);
                  }
                }}
                className="btn"
              >
                <h4 className="text">{t("start-now")}</h4>
              </Button>
            )}

            {showLogin && (
              <div className="scheduling_btn_group">
                <Button
                  onClick={handleOpenAuthModal}
                  className="btn schedule_later !mx-auto !min-w-fit"
                >
                  <h4 className="text">{t("login-to-schedule")}</h4>
                </Button>
              </div>
            )}
          </div>
          <div className="assignment_scheduling-errors">
            {submissionError && <div className="error">{submissionError}</div>}
          </div>
        </>
      </div>
    </section>
  );
}
